import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock, LogOut, Loader2, Download, RefreshCw, Search, Eye, Trash2,
  Send, X, Check, Calendar,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ConferenceNotifier } from '../components/ConferenceNotifier';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'dc_men_conference_admin_auth';

const DetailBlock = ({ title, children }) => (
  <div>
    <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider border-b border-navy-100 pb-2 mb-3">
      {title}
    </h3>
    <dl className="space-y-1.5">{children}</dl>
  </div>
);
const DetailRow = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-3 text-sm">
    <dt className="text-navy-500 font-medium">{label}</dt>
    <dd className="col-span-2 text-navy-800 break-words">{value || <span className="text-navy-300">—</span>}</dd>
  </div>
);

export default function ConferenceAdminPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [bulkSending, setBulkSending] = useState(false);

  // Verify existing session
  useEffect(() => {
    const check = async () => {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const res = await fetch(`${API_URL}/api/conference-admin/verify?password=${encodeURIComponent(stored)}`);
          const d = await res.json();
          if (d.valid) {
            setIsAuthenticated(true);
          } else {
            sessionStorage.removeItem(STORAGE_KEY);
          }
        } catch {
          sessionStorage.removeItem(STORAGE_KEY);
        }
      }
      setCheckingAuth(false);
    };
    check();
  }, []);

  const getStoredPassword = () => sessionStorage.getItem(STORAGE_KEY) || '';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const stored = getStoredPassword();
      const res = await fetch(
        `${API_URL}/api/admin/conference-registrations?password=${encodeURIComponent(stored)}`
      );
      if (res.ok) {
        setData(await res.json());
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [isAuthenticated, fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const fd = new FormData();
      fd.append('password', password);
      const res = await fetch(`${API_URL}/api/conference-admin/login`, { method: 'POST', body: fd });
      if (!res.ok) {
        toast.error('Incorrect password');
        return;
      }
      sessionStorage.setItem(STORAGE_KEY, password);
      setIsAuthenticated(true);
      setPassword('');
    } catch {
      toast.error('Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setIsAuthenticated(false);
    navigate('/');
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const stored = getStoredPassword();
      const res = await fetch(
        `${API_URL}/api/admin/conference-registrations/export?password=${encodeURIComponent(stored)}`
      );
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `conference_registrations_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Registrations exported');
    } catch {
      toast.error('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this registration?')) return;
    try {
      const stored = getStoredPassword();
      const res = await fetch(
        `${API_URL}/api/admin/conference-registrations/${id}?password=${encodeURIComponent(stored)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Registration deleted');
      setSelected(null);
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleResend = async (id) => {
    setResendingId(id);
    try {
      const stored = getStoredPassword();
      const res = await fetch(
        `${API_URL}/api/admin/conference-registrations/${id}/resend-confirmation?password=${encodeURIComponent(stored)}`,
        { method: 'POST' }
      );
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Resend failed');
      if (d.success) {
        toast.success(`Confirmation email resent to ${d.email}`);
      } else {
        const reason = d.error ? `Email failed for ${d.email}: ${d.error}` : `Email send failed for ${d.email}. Check SendGrid domain verification.`;
        toast.error(reason);
      }
      fetchData();
    } catch (e) {
      toast.error(e.message || 'Failed to resend');
    } finally {
      setResendingId(null);
    }
  };

  const handleBulkResend = async (onlyUnsent) => {
    const scopeLabel = onlyUnsent ? 'only registrants without confirmation yet' : 'ALL registrants';
    if (!window.confirm(`Resend the confirmation email to ${scopeLabel}?`)) return;
    setBulkSending(true);
    try {
      const stored = getStoredPassword();
      const res = await fetch(
        `${API_URL}/api/admin/conference-registrations/bulk-resend-confirmation?password=${encodeURIComponent(stored)}&only_unsent=${onlyUnsent}`,
        { method: 'POST' }
      );
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Bulk resend failed');
      toast.success(`Sent ${d.sent} of ${d.total}${d.failed ? ` · ${d.failed} failed` : ''}`);
      if (d.failed && d.error) {
        toast.error(`Reason for failures: ${d.error}`);
      }
      fetchData();
    } catch (e) {
      toast.error(e.message || 'Failed to send');
    } finally {
      setBulkSending(false);
    }
  };

  /* --------------------- Loading / Login screens --------------------- */

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-brand-navy mx-auto mb-4" />
          <p className="text-navy-600">Loading…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-900 via-navy-800 to-brand-navy flex items-center justify-center px-4">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-2xl bg-brand-gold/10 border border-brand-gold/30 flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-7 h-7 text-brand-gold" />
              </div>
              <h1 className="text-2xl font-bold text-navy-900">Conference Admin</h1>
              <p className="text-sm text-navy-500 mt-1">Access restricted to conference registrations.</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4" data-testid="conf-admin-login-form">
              <div>
                <label className="text-sm font-medium text-navy-700 mb-1 block">Password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter conference admin password"
                    className="pl-9"
                    data-testid="conf-admin-password"
                    autoFocus
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loginLoading || !password}
                className="w-full bg-brand-navy hover:bg-navy-700 text-white font-semibold"
                data-testid="conf-admin-login-btn"
              >
                {loginLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in…</> : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* --------------------- Authenticated dashboard --------------------- */

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold">Conference Admin</h1>
            <p className="text-navy-200 text-sm">4th Annual Citywide Conference · Oct 12, 2026 · Aspen Institute</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-white/30 text-white hover:bg-white/10 hover:text-white"
            data-testid="conf-admin-logout"
          >
            <LogOut className="w-4 h-4 mr-2" /> Log out
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-testid="conf-admin-dashboard">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-bold text-navy-900">Conference Registrations</h2>
            <p className="text-navy-500 max-w-2xl">
              View, search, and manage attendee registrations. Resend confirmation emails individually or in bulk.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={fetchData}
              variant="outline"
              disabled={loading}
              className="border-2 border-navy-200 text-navy-700 hover:bg-navy-50"
              data-testid="conf-admin-refresh"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <ConferenceNotifier
              registrations={data?.registrations || []}
              getPassword={getStoredPassword}
              onSent={fetchData}
            />
            <Button
              onClick={() => handleBulkResend(true)}
              disabled={bulkSending || !data || data.total === 0}
              variant="outline"
              className="border-2 border-brand-green text-brand-green hover:bg-green-50"
              data-testid="conf-admin-bulk-unsent"
            >
              {bulkSending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send to Unconfirmed</>}
            </Button>
            <Button
              onClick={() => handleBulkResend(false)}
              disabled={bulkSending || !data || data.total === 0}
              className="bg-brand-navy hover:bg-navy-700 text-white"
              data-testid="conf-admin-bulk-all"
            >
              {bulkSending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send to All</>}
            </Button>
            <Button
              onClick={handleExport}
              disabled={exporting || !data || data.total === 0}
              className="bg-brand-gold hover:bg-gold-600 text-navy-900"
              data-testid="conf-admin-export"
            >
              {exporting ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Exporting…</> : <><Download className="w-4 h-4 mr-2" /> Export CSV</>}
            </Button>
          </div>
        </div>

        {data && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-brand-navy to-navy-700 text-white">
                <CardContent className="p-5">
                  <p className="text-xs uppercase tracking-wider text-navy-200 mb-1">Total Registered</p>
                  <p className="text-4xl font-bold">{data.total}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <p className="text-xs uppercase tracking-wider text-navy-500 mb-2">Roles Breakdown</p>
                  {Object.keys(data.roles_breakdown || {}).length === 0 ? (
                    <p className="text-sm text-navy-400">No data yet</p>
                  ) : (
                    <ul className="text-xs text-navy-700 space-y-0.5">
                      {Object.entries(data.roles_breakdown).slice(0, 4).map(([k, v]) => (
                        <li key={k} className="flex justify-between gap-2"><span className="truncate">{k}</span><span className="font-semibold">{v}</span></li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <p className="text-xs uppercase tracking-wider text-navy-500 mb-2">Dietary Breakdown</p>
                  {Object.keys(data.dietary_breakdown || {}).length === 0 ? (
                    <p className="text-sm text-navy-400">No data yet</p>
                  ) : (
                    <ul className="text-xs text-navy-700 space-y-0.5">
                      {Object.entries(data.dietary_breakdown).slice(0, 4).map(([k, v]) => (
                        <li key={k} className="flex justify-between gap-2"><span className="truncate">{k}</span><span className="font-semibold">{v}</span></li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <p className="text-xs uppercase tracking-wider text-navy-500 mb-2">Experience</p>
                  {Object.keys(data.experience_breakdown || {}).length === 0 ? (
                    <p className="text-sm text-navy-400">No data yet</p>
                  ) : (
                    <ul className="text-xs text-navy-700 space-y-0.5">
                      {Object.entries(data.experience_breakdown).map(([k, v]) => (
                        <li key={k} className="flex justify-between gap-2"><span className="truncate">{k}</span><span className="font-semibold">{v}</span></li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="flex gap-2 max-w-md">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
                <Input
                  placeholder="Search by name, email, organization…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="conf-admin-search"
                />
              </div>
            </div>

            <Card>
              <CardContent className="p-0">
                {data.registrations.length === 0 ? (
                  <p className="p-6 text-navy-500 text-sm">No registrations yet.</p>
                ) : (
                  <div className="overflow-x-auto" data-testid="conf-admin-table">
                    <table className="min-w-full text-sm">
                      <thead className="bg-navy-50 text-navy-700">
                        <tr>
                          <th className="text-left px-4 py-3 font-semibold">Name</th>
                          <th className="text-left px-4 py-3 font-semibold">Email</th>
                          <th className="text-left px-4 py-3 font-semibold">Organization</th>
                          <th className="text-left px-4 py-3 font-semibold">City, State</th>
                          <th className="text-left px-4 py-3 font-semibold">Registered</th>
                          <th className="text-left px-4 py-3 font-semibold">Email Sent</th>
                          <th className="text-right px-4 py-3 font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.registrations
                          .filter((r) => {
                            if (!search.trim()) return true;
                            const q = search.toLowerCase();
                            return (
                              (r.first_name || '').toLowerCase().includes(q) ||
                              (r.last_name || '').toLowerCase().includes(q) ||
                              (r.email || '').toLowerCase().includes(q) ||
                              (r.organization || '').toLowerCase().includes(q)
                            );
                          })
                          .map((r) => {
                            const created = r.created_at ? new Date(r.created_at) : null;
                            const isResending = resendingId === r.id;
                            return (
                              <tr key={r.id} className="border-t border-navy-50 hover:bg-navy-50/40">
                                <td className="px-4 py-3 text-navy-900 font-medium">
                                  {[r.first_name, r.last_name].filter(Boolean).join(' ')}
                                  {r.preferred_name && <span className="ml-2 text-xs text-navy-500">({r.preferred_name})</span>}
                                </td>
                                <td className="px-4 py-3 text-navy-600">{r.email}</td>
                                <td className="px-4 py-3 text-navy-600 max-w-[220px] truncate">{r.organization || '—'}</td>
                                <td className="px-4 py-3 text-navy-600">{[r.city, r.state].filter(Boolean).join(', ') || '—'}</td>
                                <td className="px-4 py-3 text-navy-600 whitespace-nowrap">{created ? created.toLocaleDateString() : '—'}</td>
                                <td className="px-4 py-3">
                                  {r.confirmation_email_sent ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                                      <Check className="w-3 h-3" /> Sent
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">Not sent</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                  <Button size="sm" variant="ghost" onClick={() => setSelected(r)}
                                    className="text-navy-600 hover:text-navy-900 hover:bg-navy-50"
                                    data-testid={`conf-admin-view-${r.id}`}>
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => handleResend(r.id)} disabled={isResending}
                                    className="text-brand-green hover:text-green-700 hover:bg-green-50"
                                    title="Resend confirmation email"
                                    data-testid={`conf-admin-resend-${r.id}`}>
                                    {isResending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    data-testid={`conf-admin-delete-${r.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="conf-admin-detail-modal">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-brand-navy text-white p-6 rounded-t-2xl flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">{[selected.first_name, selected.last_name].filter(Boolean).join(' ')}</h2>
                <p className="text-white/70 text-sm">{selected.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => handleResend(selected.id)}
                  disabled={resendingId === selected.id}
                  size="sm"
                  className="bg-brand-green hover:bg-green-700 text-white"
                  data-testid="conf-admin-detail-resend"
                >
                  {resendingId === selected.id ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Resend Confirmation</>
                  )}
                </Button>
                <button onClick={() => setSelected(null)} className="text-white/80 hover:text-white" data-testid="conf-admin-detail-close">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-6 space-y-6 text-sm text-navy-800">
              <DetailBlock title="Contact">
                <DetailRow label="Preferred Name" value={selected.preferred_name} />
                <DetailRow label="Mobile" value={selected.mobile_phone} />
                <DetailRow label="Organization" value={selected.organization} />
                <DetailRow label="Preferred Org (Badge)" value={selected.preferred_organization} />
                <DetailRow label="Job Title" value={selected.job_title} />
                <DetailRow label="Location" value={[selected.city, selected.state].filter(Boolean).join(', ')} />
              </DetailBlock>
              <DetailBlock title="Professional">
                <DetailRow label="Roles" value={(selected.roles || []).join(', ')} />
                <DetailRow label="Other Role" value={selected.roles_other} />
                <DetailRow label="Years of Experience" value={selected.years_experience} />
              </DetailBlock>
              <DetailBlock title="Conference Experience">
                <DetailRow label="Attended Before" value={selected.attended_before} />
                <DetailRow label="Previous Years" value={(selected.previous_years || []).join(', ')} />
                <DetailRow label="Other Previous Year" value={selected.previous_years_other} />
                <DetailRow label="How Heard" value={selected.how_heard} />
                <DetailRow label="How Heard (Other)" value={selected.how_heard_other} />
              </DetailBlock>
              <DetailBlock title="Language & Demographics">
                <DetailRow label="First Language" value={selected.first_language || selected.first_language_other} />
                <DetailRow label="Race / Ethnicity" value={(selected.race_ethnicity || []).join(', ')} />
                <DetailRow label="Race / Ethnicity (Other)" value={selected.race_ethnicity_other} />
              </DetailBlock>
              <DetailBlock title="Accessibility & Dietary">
                <DetailRow label="Accommodations Needed" value={selected.needs_accommodations} />
                <DetailRow label="Accommodation Details" value={selected.accommodations_description} />
                <DetailRow label="Dietary" value={(selected.dietary_restrictions || []).join(', ')} />
                <DetailRow label="Dietary (Other)" value={selected.dietary_other} />
              </DetailBlock>
              <DetailBlock title="Engagement">
                <DetailRow label="Looking Forward To" value={(selected.looking_forward_to || []).join(', ')} />
                <DetailRow label="Future Events Info" value={selected.future_events_info} />
                <DetailRow label="First ECE Conference" value={selected.first_ece_conference} />
                <DetailRow label="Mailing List Opt-In" value={selected.mailing_list_opt_in} />
                <DetailRow label="Sponsorship Info" value={selected.sponsorship_info} />
              </DetailBlock>
              <DetailBlock title="Consent">
                <DetailRow label="Media Consent" value={selected.media_consent === 'consent' ? 'Yes — consents to photo/video' : 'No — does not consent'} />
                <DetailRow label="Agreement" value={selected.agreement ? 'Agreed' : 'Not agreed'} />
                <DetailRow label="Confirmation Email" value={selected.confirmation_email_sent ? 'Sent' : 'Not sent'} />
                <DetailRow label="Registered" value={selected.created_at ? new Date(selected.created_at).toLocaleString() : ''} />
              </DetailBlock>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
